/**
 * Pure helpers for `DateField` (spec 073 B1): DD.MM.YYYY text, ISO `YYYY-MM-DD`
 * values and a Monday-first month grid. Everything works on ISO strings with
 * UTC arithmetic, never on a local `Date`, so the output does not depend on the
 * environment's locale or time zone.
 */

const ISO = /^(\d{4})-(\d{2})-(\d{2})$/

export function daysInMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate()
}

function pad(n: number, width = 2): string {
  return String(n).padStart(width, '0')
}

export function toIso(year: number, month: number, day: number): string {
  return `${pad(year, 4)}-${pad(month)}-${pad(day)}`
}

/** `{year, month (1-12), day}` of a real calendar ISO date, else `null`. */
export function parseIso(iso: string): { year: number; month: number; day: number } | null {
  const m = ISO.exec(iso)
  if (m === null) return null
  const year = Number(m[1])
  const month = Number(m[2])
  const day = Number(m[3])
  if (month < 1 || month > 12 || day < 1 || day > daysInMonth(year, month)) return null
  return { year, month, day }
}

/** `2026-10-21` -> `21.10.2026`; anything that is not a real ISO date -> `null`. */
export function isoToDisplay(iso: string): string | null {
  const p = parseIso(iso)
  return p === null ? null : `${pad(p.day)}.${pad(p.month)}.${pad(p.year, 4)}`
}

/** `21.10.2026` -> `2026-10-21`. A real calendar date is required (`31.02.2026` and `1.1` are `null`). */
export function displayToIso(text: string): string | null {
  const m = /^(\d{2})\.(\d{2})\.(\d{4})$/.exec(text.trim())
  if (m === null) return null
  const iso = toIso(Number(m[3]), Number(m[2]), Number(m[1]))
  return parseIso(iso) === null ? null : iso
}

/**
 * Typing filter: keeps digits and dots, auto-inserts the dots after the day and
 * month when digits are typed forward (not on deletion), caps at 10 characters.
 */
export function normalizeTyping(next: string, prev: string): string {
  const cleaned = next.replace(/[^\d.]/g, '').replace(/\.{2,}/g, '.')
  let out = cleaned
  if (!cleaned.includes('.')) {
    const d = cleaned.slice(0, 8)
    const parts = [d.slice(0, 2), d.slice(2, 4), d.slice(4, 8)].filter((p) => p !== '')
    out = parts.join('.')
  }
  if (cleaned.length > prev.length && /^\d{2}(\.\d{2})?$/.test(out)) out += '.'
  return out.slice(0, 10)
}

/** Monday = 0 ... Sunday = 6. */
export function weekdayIndex(iso: string): number {
  const p = parseIso(iso)
  if (p === null) return 0
  return (new Date(Date.UTC(p.year, p.month - 1, p.day)).getUTCDay() + 6) % 7
}

export function addDays(iso: string, n: number): string {
  const p = parseIso(iso)
  if (p === null) return iso
  const d = new Date(Date.UTC(p.year, p.month - 1, p.day + n))
  return toIso(d.getUTCFullYear(), d.getUTCMonth() + 1, d.getUTCDate())
}

/** Whole months, the day clamped to the target month's length. */
export function addMonths(iso: string, n: number): string {
  const p = parseIso(iso)
  if (p === null) return iso
  const index = p.year * 12 + (p.month - 1) + n
  const year = Math.floor(index / 12)
  const month = (index % 12) + 1
  return toIso(year, month, Math.min(p.day, daysInMonth(year, month)))
}

export interface GridCell {
  iso: string
  day: number
  inMonth: boolean
}

/** Six Monday-first weeks covering `month` (1-12). */
export function monthGrid(year: number, month: number): GridCell[][] {
  const first = toIso(year, month, 1)
  const start = addDays(first, -weekdayIndex(first))
  const weeks: GridCell[][] = []
  for (let w = 0; w < 6; w += 1) {
    const row: GridCell[] = []
    for (let d = 0; d < 7; d += 1) {
      const iso = addDays(start, w * 7 + d)
      const p = parseIso(iso)
      row.push({ iso, day: p?.day ?? 0, inMonth: p !== null && p.month === month && p.year === year })
    }
    weeks.push(row)
  }
  return weeks
}

/** In range: `min`/`max` are inclusive ISO bounds, `null` is open on that side (ISO strings compare lexically). */
export function isAllowed(iso: string, min: string | null, max: string | null): boolean {
  if (min !== null && iso < min) return false
  if (max !== null && iso > max) return false
  return true
}

export function clampIso(iso: string, min: string | null, max: string | null): string {
  if (min !== null && iso < min) return min
  if (max !== null && iso > max) return max
  return iso
}

/** The focused day after a key press, clamped to the allowed range; `null` when the key moves nothing. */
export function moveFocus(
  iso: string,
  key: string,
  shift: boolean,
  min: string | null,
  max: string | null,
): string | null {
  let next: string
  switch (key) {
    case 'ArrowLeft':
      next = addDays(iso, -1)
      break
    case 'ArrowRight':
      next = addDays(iso, 1)
      break
    case 'ArrowUp':
      next = addDays(iso, -7)
      break
    case 'ArrowDown':
      next = addDays(iso, 7)
      break
    case 'PageUp':
      next = addMonths(iso, shift ? -12 : -1)
      break
    case 'PageDown':
      next = addMonths(iso, shift ? 12 : 1)
      break
    case 'Home':
      next = addDays(iso, -weekdayIndex(iso))
      break
    case 'End':
      next = addDays(iso, 6 - weekdayIndex(iso))
      break
    default:
      return null
  }
  return clampIso(next, min, max)
}

/**
 * When the visible month has no allowed day, the nearest month that has one
 * (`YYYY-MM-01`); `null` when the visible month already has an allowed day.
 */
export function nearestAllowedMonth(year: number, month: number, min: string | null, max: string | null): string | null {
  const first = toIso(year, month, 1)
  const last = toIso(year, month, daysInMonth(year, month))
  if (max !== null && first > max) return `${max.slice(0, 7)}-01`
  if (min !== null && last < min) return `${min.slice(0, 7)}-01`
  return null
}

export const WEEKDAY_LABELS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'] as const
export const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
] as const

/** `Stored data: A to B`, `from A`, `up to B`, or `null` when unbounded. */
export function boundsFooter(min: string | null, max: string | null): string | null {
  const a = min === null ? null : isoToDisplay(min)
  const b = max === null ? null : isoToDisplay(max)
  if (a !== null && b !== null) return `Stored data: ${a} to ${b}`
  if (a !== null) return `Stored data: from ${a}`
  if (b !== null) return `Stored data: up to ${b}`
  return null
}
